import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useSite } from '../context/SiteContext';
import { trackContactEvent } from '../utils/trackingEvents';

const FALLBACK = {
  title: 'Sobre a Sperandio Oral Center',
  lead: 'Há mais de 15 anos cuidando do sorriso e da saúde bucal de nossos pacientes com dedicação, tecnologia e excelência profissional.',
  established: 'Nossa clínica nasceu do sonho de oferecer tratamentos odontológicos de qualidade, combinando técnicas modernas com atendimento humanizado e personalizado.',
  heroImage: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&h=400&fit=crop',
  mission: 'Oferecer tratamentos odontológicos de excelência com tecnologia avançada, priorizando o conforto e bem-estar dos nossos pacientes em um ambiente acolhedor e moderno.',
  vision: 'Ser reconhecida como referência em odontologia de qualidade, proporcionando sorrisos saudáveis e transformando vidas através de cuidados especializados.',
  values: 'Excelência em atendimento, Tecnologia de ponta, Ética profissional, Conforto do paciente, Resultados duradouros.',
  coreValues: [
    { icon: 'fas fa-heart', title: 'Cuidado Humanizado', desc: 'Tratamos cada paciente com carinho, atenção e respeito, priorizando seu bem-estar.' },
    { icon: 'fas fa-microscope', title: 'Tecnologia Avançada', desc: 'Utilizamos equipamentos de última geração para diagnósticos precisos e tratamentos eficazes.' },
    { icon: 'fas fa-award', title: 'Excelência Profissional', desc: 'Nossa equipe é formada por especialistas qualificados e em constante atualização.' },
    { icon: 'fas fa-handshake', title: 'Confiança e Transparência', desc: 'Mantemos comunicação clara sobre tratamentos, custos e expectativas.' },
  ],
  history: [
    'A Sperandio Oral Center foi fundada em 2008 pelo Dr. João Sperandio, com o objetivo de oferecer tratamentos odontológicos de alta qualidade em um ambiente acolhedor e moderno.',
    'Ao longo dos anos, expandimos nossa equipe com especialistas renomados e investimos continuamente em tecnologia de ponta para oferecer os melhores resultados aos nossos pacientes.',
    'Hoje, somos uma referência em odontologia, atendendo mais de 5000+ pacientes que confiam em nosso trabalho e recomendam nossos serviços.',
  ],
  historyImage: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=600&h=400&fit=crop',
  gallery: [
    { src: 'https://sperandiooralcenter.com.br/wp-content/uploads/2025/03/Imagem-do-WhatsApp-de-2025-03-27-as-10.09.54_38dc4e61-scaled-1.jpg', title: 'Recepção da Clínica', subtitle: 'Ambiente acolhedor e moderno' },
    { src: 'https://sperandiooralcenter.com.br/wp-content/uploads/2025/03/Imagem-do-WhatsApp-de-2025-03-27-as-10.09.54_23fdc788-scaled-1.jpg', title: 'Consultório Odontológico', subtitle: 'Equipamentos de última geração' },
    { src: 'https://sperandiooralcenter.com.br/wp-content/uploads/2025/03/Imagem-do-WhatsApp-de-2025-03-27-as-10.09.54_0048e368-scaled-1.jpg', title: 'Área de Atendimento', subtitle: 'Conforto e tecnologia' },
    { src: 'https://sperandiooralcenter.com.br/wp-content/uploads/2025/03/MAPA-e1742862323929.png', title: 'Nossa Localização', subtitle: 'Centro de Apucarana - PR' },
  ],
  differentials: [
    { icon: 'fas fa-microscope', label: 'Tecnologia Avançada' },
    { icon: 'fas fa-user-md', label: 'Profissionais Qualificados' },
    { icon: 'fas fa-heart', label: 'Atendimento Humanizado' },
    { icon: 'fas fa-shield-alt', label: 'Segurança e Higiene' },
  ],
};

const About: React.FC = () => {
  const { config, getPage, imageUrl, whatsappUrl, galleryPhotos } = useSite();
  const page = getPage('about') || getPage('sobre');

  const title = page?.page_title || `Sobre ${config?.site_title || 'a Sperandio Oral Center'}`;
  const lead = page?.page_description || config?.about_text || FALLBACK.lead;
  const established = config?.established_text || FALLBACK.established;

  const heroImg = page?.photos?.[0]?.image_path
    ? imageUrl(page.photos[0].image_path)
    : config?.hero_image
      ? imageUrl(config.hero_image)
      : FALLBACK.heroImage;

  const mission = config?.mission || FALLBACK.mission;
  const vision = config?.vision || FALLBACK.vision;
  const values = config?.values || FALLBACK.values;

  const sections = page?.sections || [];
  const coreValuesSection = sections.find(s => s.section_key === 'core_values' || s.section_title?.includes('move'));
  const historySection = sections.find(s => s.section_key === 'history' || s.section_title?.includes('História'));

  const coreValues = coreValuesSection?.items?.length
    ? coreValuesSection.items.map(i => ({ icon: i.item_icon || '', title: i.item_title || '', desc: i.item_description || '' }))
    : FALLBACK.coreValues;

  const historyParagraphs = historySection?.section_content
    ? historySection.section_content.split('\n')
    : FALLBACK.history;

  const historyImg = historySection?.section_image
    ? imageUrl(historySection.section_image)
    : FALLBACK.historyImage;

  const photos = galleryPhotos.length > 0
    ? galleryPhotos.map(p => ({ src: imageUrl(p.image_path), title: '', subtitle: '' }))
    : FALLBACK.gallery;

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="bg-profile-2 py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h1 className="display-4 text-profile-1 fw-bold mb-4">{title}</h1>
              <p className="lead text-profile-4 mb-4">{lead}</p>
              <p className="text-profile-4">{established}</p>
            </Col>
            <Col lg={6}>
              <img alt="Clínica Sperandio" className="img-fluid rounded shadow" src={heroImg} />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Missão, Visão, Valores */}
      <section className="py-5">
        <Container>
          <Row>
            {[
              { icon: 'fas fa-bullseye', label: 'Nossa Missão', text: mission },
              { icon: 'fas fa-eye', label: 'Nossa Visão', text: vision },
              { icon: 'fas fa-gem', label: 'Nossos Valores', text: values },
            ].map((item, i) => (
              <Col lg={4} className="mb-4" key={i}>
                <Card className="h-100 text-center border-0 shadow-sm hover-card">
                  <Card.Body className="p-4">
                    <div className="bg-profile-3 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                      <i className={`${item.icon} text-white fs-2`}></i>
                    </div>
                    <Card.Title className="text-profile-1 h4">{item.label}</Card.Title>
                    <Card.Text className="text-profile-4">{item.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* O que nos move */}
      <section className="bg-profile-5 py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="text-profile-1 fw-bold mb-3">
                {coreValuesSection?.section_title || 'O que nos move'}
              </h2>
              <p className="lead text-profile-4">
                {coreValuesSection?.section_subtitle || 'Nossos valores fundamentais que guiam cada atendimento'}
              </p>
            </Col>
          </Row>
          <Row>
            {coreValues.map((item, i) => (
              <Col lg={6} className="mb-4" key={i}>
                <div className="d-flex align-items-start">
                  <div className="bg-profile-1 text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '60px', height: '60px' }}>
                    <i className={`${item.icon} fs-4`}></i>
                  </div>
                  <div>
                    <h5 className="text-profile-1 mb-2">{item.title}</h5>
                    <p className="text-profile-4 mb-0">{item.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Nossa História */}
      <section className="py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <img alt="História da clínica" className="img-fluid rounded shadow" src={historyImg} />
            </Col>
            <Col lg={6}>
              <h2 className="text-profile-1 fw-bold mb-4">
                {historySection?.section_title || 'Nossa História'}
              </h2>
              {historyParagraphs.map((p, i) => (
                <p key={i} className="text-profile-4 mb-3">{p}</p>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Galeria + Diferenciais */}
      <section className="py-5 bg-profile-5">
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 className="text-profile-1 fw-bold mb-3">Conheça Nossa Clínica</h2>
              <p className="text-muted lead">Ambiente moderno e acolhedor para seu conforto e bem-estar</p>
            </Col>
          </Row>
          <Row className="g-4">
            {photos.map((photo, i) => (
              <Col lg={6} key={i}>
                <Card className="shadow-sm border-0 hover-card h-100">
                  <div className="position-relative overflow-hidden" style={{ height: '300px' }}>
                    <img className="card-img-top h-100 w-100" alt={photo.title || 'Clínica'} src={photo.src} style={{ objectFit: 'cover' }} />
                    {photo.title && (
                      <div className="position-absolute bottom-0 start-0 end-0 bg-gradient-dark p-3">
                        <h5 className="text-white fw-bold mb-1">{photo.title}</h5>
                        {photo.subtitle && <p className="text-white-50 mb-0 small">{photo.subtitle}</p>}
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="mt-5">
            <Col lg={12} className="text-center">
              <div className="bg-profile-1 text-white p-4 rounded">
                <h4 className="fw-bold mb-3">
                  <i className="fas fa-award me-2 text-profile-3"></i>Diferenciais da Nossa Clínica
                </h4>
                <Row className="g-3">
                  {FALLBACK.differentials.map((d, i) => (
                    <Col md={3} key={i}>
                      <div className="d-flex flex-column align-items-center">
                        <i className={`${d.icon} fs-2 text-profile-3 mb-2`}></i>
                        <span className="small">{d.label}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>

        <style>{`
          .bg-gradient-dark {
            background: linear-gradient(transparent, rgba(0,0,0,0.8));
          }
          .hover-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(20, 63, 63, 0.2);
          }
        `}</style>
      </section>

      {/* CTA */}
      <section className="bg-profile-1 text-white py-5">
        <Container>
          <Row className="text-center">
            <Col>
              <h3 className="mb-3">Faça parte da nossa história</h3>
              <p className="mb-4">Agende sua consulta e descubra por que somos a escolha de milhares de pacientes</p>
              <Button
                variant="light"
                size="lg"
                className="text-profile-1 rounded-pill px-5"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackContactEvent}
              >
                <i className="far fa-calendar-alt me-2"></i>Agendar Consulta
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default About;
